// apps/workflow/src/pages/freelancer/Payments.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DollarSign, Calendar, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

export default function FreelancerPayments() {
  const { user } = useAuth();
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchEarnings = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const records = await pb.collection('designer_earnings').getFullList({
        filter: `designerId = "${user.id}"`,
        sort: '-weekStart',
        expand: 'taskId,projectId',
      });
      setEarnings(records);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      if (error.status !== 404) toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEarnings(); }, [user]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchEarnings();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  // Group by week (weekStart)
  const groupedEarnings = earnings.reduce((acc, e) => {
    const key = e.weekStart;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  const sortedWeeks = Object.keys(groupedEarnings).sort((a, b) => new Date(b) - new Date(a));

  const weekSummaries = sortedWeeks.map(weekStart => {
    const items = groupedEarnings[weekStart];
    const total = items.reduce((sum, e) => sum + (e.amount || 0), 0);
    const allPaid = items.every(e => e.status === 'paid');
    const anyPending = items.some(e => e.status === 'pending');
    const status = allPaid ? 'paid' : (anyPending ? 'pending' : 'unknown');
    const weekEnd = items[0]?.weekEnd || '';
    return { weekStart, weekEnd, items, total, status };
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading payments...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-amber-500" />
            My Payments
          </h1>
          <p className="text-sm text-slate-500">Bi‑weekly earning summaries and payment status</p>
        </div>
        <button onClick={handleRefresh} disabled={refreshing} className="p-2 text-slate-400 hover:text-amber-600 transition rounded-lg hover:bg-amber-50 disabled:opacity-50">
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-xl p-5 border border-indigo-200/50">
          <p className="text-xs font-medium text-indigo-600 uppercase tracking-wider">Total Earned</p>
          <p className="text-2xl font-bold text-indigo-700 mt-1">₹{earnings.reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-5 border border-green-200/50">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Paid</p>
          <p className="text-2xl font-bold text-green-700 mt-1">₹{earnings.filter(e => e.status === 'paid').reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-xl p-5 border border-amber-200/50">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Pending</p>
          <p className="text-2xl font-bold text-amber-700 mt-1">₹{earnings.filter(e => e.status === 'pending').reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</p>
        </div>
      </div>

      {/* Weekly Breakdown */}
      {weekSummaries.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-200">
          <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No earnings recorded yet.</p>
          <p className="text-xs">Complete tasks to start earning.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {weekSummaries.map((week) => {
            const isPaid = week.status === 'paid';
            const statusColor = isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700';
            const statusLabel = isPaid ? '✅ Paid' : '⏳ Pending';
            const weekEndDate = new Date(week.weekEnd);
            const now = new Date();
            const diffDays = Math.floor((now - weekEndDate) / (1000 * 60 * 60 * 24));
            const canRequest = !isPaid && diffDays >= 14;

            return (
              <div key={week.weekStart} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-800 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-indigo-500" />
                      Week: {formatDate(week.weekStart)} – {formatDate(week.weekEnd)}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">{week.items.length} earning entry(ies)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-indigo-600">₹{week.total.toFixed(2)}</p>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor}`}>{statusLabel}</span>
                    {canRequest && (
                      <button className="ml-2 px-3 py-1 bg-amber-500 text-white rounded-lg text-sm hover:bg-amber-600 transition">
                        Request Payment
                      </button>
                    )}
                  </div>
                </div>
                {/* Individual entries */}
                <details className="mt-2">
                  <summary className="text-sm text-indigo-600 cursor-pointer">View details</summary>
                  <div className="mt-2 space-y-2">
                    {week.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1">
                        <span className="text-slate-600">{item.expand?.taskId?.title || 'Task'}</span>
                        <span className="font-mono">₹{item.amount?.toFixed(2)}</span>
                        <span className={`text-xs ${item.status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}